import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { Text } from "../../elements/text/text";
import { Column, Container, Row } from "../../layout/grid";
import type {
  BlockInspectorProps,
  FieldInspectorProps,
} from "../../lib/inspector";
import { cn } from "../../lib/utils";
import type {
  CarouselConfigurationProps,
  CarouselNavigationLabels,
  CarouselProps,
} from "../../patterns/carousel/carousel";
import { Carousel } from "../../patterns/carousel/carousel";

export type ContentSliderBlockInspectorProps = BlockInspectorProps;

export interface CarouselBlockB2BProps extends CarouselProps {
  mode?: "B2B";
  title: string;
  subtitle?: string;
  description?: string;
}

const carouselD2CVariants = cva("", {
  variants: {
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
  },
  defaultVariants: {
    backgroundColor: "white",
  },
});

const carouselContainerVariants = cva("lg:px-16 xl:px-20", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
  },
  defaultVariants: {
    columnWidth: 12,
  },
});

/**
 * Configuration shared between the presentational D2C carousel and the
 * Contentful page-block representation. Deliberately free of any knowledge
 * about the slide content type — see {@link CarouselSlide}.
 */
export interface CarouselBlockD2CConfig
  extends VariantProps<typeof carouselD2CVariants>,
    CarouselConfigurationProps,
    Partial<CarouselNavigationLabels> {
  columnWidth?: 8 | 10 | 12;
  backgroundColor?: "black" | "white" | "whitesmoke";
  headlineColor?: "black" | "white" | "alabastergrey";
  color?: "black" | "white" | "alabastergrey";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  className?: string;
  sys?: { id?: string };
  inspectorProps?: ContentSliderBlockInspectorProps;
}

/**
 * A single, already-rendered slide. The carousel is agnostic about what the
 * content is — the consumer (e.g. the Contentful `ContentSliderBlock` via the
 * content-block registry) decides how each slide is rendered, mirroring how the
 * Accordion block renders arbitrary children.
 */
export interface CarouselSlide {
  id: string;
  content: ReactNode;
  inspectorProps?: FieldInspectorProps;
}

export interface CarouselBlockD2CProps extends CarouselBlockD2CConfig {
  mode: "D2C";
  slides: CarouselSlide[];
  title?: never;
  subtitle?: never;
  description?: never;
  button?: never;
}

export type CarouselBlockProps = CarouselBlockB2BProps | CarouselBlockD2CProps;

export function CarouselBlock(props: Readonly<CarouselBlockProps>) {
  if (props.mode === "D2C") {
    if (props.slides.length === 0) {
      return null;
    }

    const isDarkBackground = props.backgroundColor === "black";
    const renderSlide = (slide: CarouselSlide, index: number) => (
      <div
        className="min-w-0 shrink-0 grow-0 basis-full pl-2"
        data-testid="content-slider-slide"
        key={slide.id ?? index}
        {...slide.inspectorProps}
      >
        {slide.content}
      </div>
    );

    return (
      <Container
        className={cn(
          "component-content-slider",
          carouselD2CVariants({
            backgroundColor: props.backgroundColor,
          }),
          props.className
        )}
        fullWidth
        outerContainerOnly
        paddingBottom={props.paddingBottom}
        paddingTop={props.paddingTop}
        testId="content-slider-container"
        {...props.inspectorProps?.container}
      >
        <Row className="gap-0">
          <Column
            className={carouselContainerVariants({
              columnWidth: props.columnWidth,
            })}
          >
            {props.slides.length === 1 ? (
              renderSlide(props.slides[0], 0)
            ) : (
              <Carousel
                activeDotClassName={isDarkBackground ? "bg-white" : undefined}
                autoplay={props.autoplay}
                autoplayDelay={props.autoplayDelay}
                behavior={props.behavior}
                dotClassName={isDarkBackground ? "bg-white/30" : undefined}
                navigationLayout={props.navigationLayout}
                navigationMinItems={2}
                navigationPlacement="sides"
                nextSlideText={props.nextSlideText ?? "Next slide"}
                previousSlideText={props.previousSlideText ?? "Previous slide"}
                progressIndicatorClassName={
                  isDarkBackground ? "bg-white" : undefined
                }
                progressTrackClassName={
                  isDarkBackground ? "bg-white/30" : undefined
                }
                showNavigationArrows={props.showNavigationArrows}
                showPagination
                sideNavigationPosition="outside"
              >
                {props.slides.map(renderSlide)}
              </Carousel>
            )}
          </Column>
        </Row>
      </Container>
    );
  }

  return (
    <section className="w-full p-4">
      {/* Header Section */}
      <div className="mt-6 mb-6">
        <Text variant="h2">{props.title}</Text>
        {props.subtitle && <Text variant="body-md">{props.subtitle}</Text>}
        {props.description && (
          <Text variant="body-md">{props.description}</Text>
        )}
      </div>

      <Carousel {...props} />
    </section>
  );
}
